import i18nFactory from '@yandex-int/i18n';
import { get } from 'lodash';

import { EditPricePackageErrorsRecord, PricePackageId } from '../../../typings/pricePackages';
import { ErrorOrWarning } from '../../ErrorHelpers/ErrorHelpers';
import { createErrorsMapper } from '../../ErrorsMapper/ErrorsMapper';
import { formatNumber } from '../../FormatHelpers/FormatHelpers';

import * as keyset from './PricePackagesErrorMapper.i18n';

const i18n = i18nFactory(keyset);

const getDataFromSeparatePath = (path: string, index: number): string => {
    return path.split(/[\]\[]/)[index];
};

const extractPackageIndexFromErrorPath = (path: string): number => {
    return Number(getDataFromSeparatePath(path, 1));
};

const extractPackageIdFromErrorPath = (path: string, packagesIds: PricePackageId[]): PricePackageId => {
    const packageId = extractPackageIndexFromErrorPath(path);

    return packagesIds[packageId];
};

const extractPackageClientIdFromErrorPath = (path: string, clientsIds: string[]): string => {
    const clientId = Number(getDataFromSeparatePath(path, 3));

    return clientsIds[clientId];
};

const extractFieldNameFromErrorPath = (path: string): PricePackageId => {
    return getDataFromSeparatePath(path, 2).split('.')[1];
};

export const mapPricePackagesValidationErrors = (
    errors: ErrorOrWarning[],
    packagesIds: PricePackageId[]
): EditPricePackageErrorsRecord => {
    return errors.reduce((acc: EditPricePackageErrorsRecord, error: ErrorOrWarning): EditPricePackageErrorsRecord => {
        const path = error.path;
        const packageId = extractPackageIdFromErrorPath(path, packagesIds);
        const fieldName = extractFieldNameFromErrorPath(path) || 'commonPackagesErrors';

        if (acc[packageId]) {
            return {
                ...acc,
                [packageId]: {
                    ...acc[packageId],
                    [fieldName]: {
                        text: packagePriceErrorMapper({ ...error, path: fieldName }),
                        path: fieldName
                    }
                }
            };
        }

        return {
            ...acc,
            [packageId]: {
                [fieldName]: {
                    text: packagePriceErrorMapper({ ...error, path: fieldName }),
                    path: fieldName
                }
            }
        };
    }, {});
};

export const compactPricePackagesClientsError = (
    errors: ErrorOrWarning[],
    clientIds: Array<string[]>
): ErrorOrWarning[] => {
    const invalidClientsIds = {};
    const invalidClientsCurrencyIds = {};

    const errorsWithoutClients = errors.reduce((acc: ErrorOrWarning[], error: ErrorOrWarning) => {
        const path = error.path;

        if (extractFieldNameFromErrorPath(path) === 'clientIds') {
            const packageIndex = extractPackageIndexFromErrorPath(path);

            if (!clientIds[packageIndex].length) {
                return acc;
            }

            const clientId = extractPackageClientIdFromErrorPath(error.path, clientIds[packageIndex]);

            if (error.code === 'DefectIds.OBJECT_NOT_FOUND') {
                if (invalidClientsIds[packageIndex]) {
                    invalidClientsIds[packageIndex].push(clientId);
                } else {
                    invalidClientsIds[packageIndex] = [clientId];
                }
            }

            if (error.code === 'PricePackageDefectIds.Gen.CLIENT_CURRENCY_NOT_EQUALS_PACKAGE_CURRENCY') {
                if (invalidClientsCurrencyIds[packageIndex]) {
                    invalidClientsCurrencyIds[packageIndex].push(clientId);
                } else {
                    invalidClientsCurrencyIds[packageIndex] = [clientId];
                }
            }

            if (error.code === 'DefectIds.FORBIDDEN_TO_CHANGE') {
                acc.push(error);
            }

            return acc;
        }

        acc.push(error);

        return acc;
    }, []);

    const compactErrorsClientId = Object.keys(invalidClientsIds).map(key => {
        return {
            code: 'DefectIds.OBJECT_NOT_FOUND',
            params: {
                clientIds: invalidClientsIds[key]
            },
            path: `updateItems[${key}].clientsIds`
        };
    });
    const compactErrorsClientCurrency = Object.keys(invalidClientsCurrencyIds).map(key => {
        return {
            code: 'PricePackageDefectIds.Gen.CLIENT_CURRENCY_NOT_EQUALS_PACKAGE_CURRENCY',
            params: {
                clientIds: invalidClientsCurrencyIds[key]
            },
            path: `updateItems[${key}].clientsCurrency`
        };
    });

    return [...errorsWithoutClients, ...compactErrorsClientId, ...compactErrorsClientCurrency];
};

export const packagePriceErrorMapper = createErrorsMapper<{}>(
    ({ idx }) => [
        {
            path: ['title'],
            codes: [
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (): string => i18n('name-cannot-be-empty')
                }
            ]
        },
        {
            path: ['title'],
            codes: [
                {
                    code: 'StringDefectIds.LENGTH_CANNOT_BE_MORE_THAN_MAX',
                    message: (err): string =>
                        i18n('name-greater-than-max', {
                            value: formatNumber(get(err, 'params.maxLength', 0))
                        })
                }
            ]
        },
        {
            path: ['title'],
            codes: [
                {
                    code: 'StringDefectIds.MUST_CONTAIN_ONLY_SYMBOLS',
                    message: (err): string => i18n('name-contain-only-symbols')
                }
            ]
        },
        {
            path: ['orderVolumeMin'],
            codes: [
                {
                    code: 'NumberDefectIds.MUST_BE_GREATER_THAN_MIN',
                    message: (err): string =>
                        i18n('show-min-less-then-min', {
                            value: formatNumber(get(err, 'params.min', 0))
                        })
                }
            ]
        },
        {
            path: ['orderVolumeMax'],
            codes: [
                {
                    code: 'NumberDefectIds.MUST_BE_GREATER_THAN_OR_EQUAL_TO_MIN',
                    message: (): string => i18n('show-max-less-then-show-min')
                }
            ]
        },
        {
            path: ['orderVolumeMax'],
            codes: [
                {
                    code: 'NumberDefectIds.MUST_BE_GREATER_THAN_MIN',
                    message: (err): string =>
                        i18n('show-max-less-then-min', {
                            value: formatNumber(get(err, 'params.min', 0))
                        })
                }
            ]
        },
        {
            path: ['price'],
            codes: [
                {
                    code: 'BidsDefects.CurrencyAmountDefects.CPM_PRICE_IS_NOT_GREATER_THAN_MIN',
                    message: (err): string =>
                        i18n('price-less-then-min', {
                            value: formatNumber(get(err, 'params.moneyPriceValue', 0))
                        })
                }
            ]
        },
        {
            path: ['price'],
            codes: [
                {
                    code: 'BidsDefects.CurrencyAmountDefects.CPM_PRICE_IS_NOT_SMALLER_THAN_MAX',
                    message: (err): string =>
                        i18n('price-greater-then-max', {
                            value: formatNumber(get(err, 'params.moneyPriceValue', 0))
                        })
                }
            ]
        },
        {
            path: ['clientsIds'],
            codes: [
                {
                    code: 'DefectIds.OBJECT_NOT_FOUND',
                    message: (err): string =>
                        i18n('invalid-client-ids', {
                            value: get(err, 'params.clientIds', []).join(', '),
                            count: get(err, 'params.clientIds', []).length
                        })
                }
            ]
        },
        {
            path: ['clientIds'],
            codes: [
                {
                    code: 'DefectIds.FORBIDDEN_TO_CHANGE',
                    message: (): string => i18n('package-forbidden-to-change')
                }
            ]
        },
        {
            path: ['clientsCurrency'],
            codes: [
                {
                    code: 'PricePackageDefectIds.Gen.CLIENT_CURRENCY_NOT_EQUALS_PACKAGE_CURRENCY',
                    message: (err): string =>
                        i18n('invalid-client-currency', {
                            value: get(err, 'params.clientIds', []).join(', ')
                        })
                }
            ]
        },
        {
            path: ['trackerUrl'],
            codes: [
                {
                    code: 'StringDefectIds.CANNOT_BE_EMPTY',
                    message: (err): string => i18n('tracker-url-cannot-be-empty')
                }
            ]
        },
        {
            path: ['trackerUrl'],
            codes: [
                {
                    code: 'DefectIds.INVALID_VALUE',
                    message: (err): string => i18n('tracker-url-invalid')
                }
            ]
        },
        {
            path: ['commonPackagesErrors'],
            codes: [
                {
                    code: 'API_ERROR',
                    message: (err): string => i18n('try-later')
                }
            ]
        },
        {
            path: ['targetingsFixed'],
            codes: [
                {
                    code: 'RegionIdDefectIds.RegionIds.INCORRECT_REGIONS',
                    message: (err): string => i18n('incorrect-region')
                },
                {
                    code: 'RegionIdDefectIds.Gen.EMPTY_REGIONS',
                    message: (err): string => i18n('empty-region')
                },
                {
                    code: 'DefectIds.CANNOT_BE_NULL',
                    message: (err): string => i18n('empty-region')
                }
            ]
        },
        {
            path: ['targetingsCustom'],
            codes: [
                {
                    code: 'RegionIdDefectIds.RegionIds.INCORRECT_REGIONS',
                    message: (err): string => i18n('incorrect-region')
                },
                {
                    code: 'RegionIdDefectIds.Gen.EMPTY_REGIONS',
                    message: (err): string => i18n('empty-region')
                },
                {
                    code: 'DefectIds.CANNOT_BE_NULL',
                    message: (err): string => i18n('empty-region')
                }
            ]
        }
    ],
    error => i18n('error-message')
);
